import type { ImageSourcePropType } from 'react-native';

/** Placeholder keys stored in recipe.images[0] for built-in assets */
export const RECIPE_PLACEHOLDER_KEYS = [
  'default',
  'main_dish',
  'seafood',
  'soup',
  'breakfast',
  'salad',
  'dessert',
  'beverages',
  'vegetarian',
  'pasta',
  'asian',
] as const;

export type RecipePlaceholderKey = (typeof RECIPE_PLACEHOLDER_KEYS)[number];

/** Labels for each placeholder (used in image picker) */
export const RECIPE_PLACEHOLDER_LABELS: Record<RecipePlaceholderKey, string> = {
  default: 'Default',
  asian: 'Asian',
  beverages: 'Beverages',
  breakfast: 'Breakfast',
  dessert: 'Dessert',
  main_dish: 'Main Dish',
  pasta: 'Pasta',
  salad: 'Salad',
  seafood: 'Seafood',
  soup: 'Soup',
  vegetarian: 'Vegetarian',
};

/** Map placeholder keys to bundled asset sources (require must be static) */
export const RECIPE_PLACEHOLDER_SOURCES: Record<RecipePlaceholderKey, ImageSourcePropType> = {
  default: require('@/assets/recipe_placeholder_assets/default.png'),
  asian: require('@/assets/recipe_placeholder_assets/asian.png'),
  beverages: require('@/assets/recipe_placeholder_assets/beverages.png'),
  breakfast: require('@/assets/recipe_placeholder_assets/breakfast.png'),
  dessert: require('@/assets/recipe_placeholder_assets/dessert.png'),
  main_dish: require('@/assets/recipe_placeholder_assets/main_dish.png'),
  pasta: require('@/assets/recipe_placeholder_assets/pasta.png'),
  salad: require('@/assets/recipe_placeholder_assets/salad.png'),
  seafood: require('@/assets/recipe_placeholder_assets/seafood.png'),
  soup: require('@/assets/recipe_placeholder_assets/soup.png'),
  vegetarian: require('@/assets/recipe_placeholder_assets/vegetarian.png'),
};

function isPlaceholderKey(key: string): key is RecipePlaceholderKey {
  return RECIPE_PLACEHOLDER_KEYS.includes(key as RecipePlaceholderKey);
}

/**
 * Get the image source for a recipe's primary image.
 * - Empty images[]: returns null (caller should show placeholder UI)
 * - Placeholder key (e.g. 'asian'): returns local require() source
 * - URL string: returns { uri } (for future upload support)
 */
export function getRecipeImageSource(images: string[]): ImageSourcePropType | null {
  const first = images?.[0];
  if (!first) return null;

  if (isPlaceholderKey(first)) {
    return RECIPE_PLACEHOLDER_SOURCES[first];
  }

  // Future: custom uploaded URLs
  if (first.startsWith('http://') || first.startsWith('https://')) {
    return { uri: first };
  }

  return null;
}
